<script lang="ts" setup>
import { MagnifyingGlassIcon, XMarkIcon } from "@heroicons/vue/24/outline";
import { computed, nextTick, onBeforeUnmount, onMounted, ref } from "vue";
import { useI18n } from "vue-i18n";
import { type ACL } from "../pinia/auth";

import CheckboxDecorator from "./CheckboxDecorator.vue";

const props = defineProps<{
  modelValue: string[];
  acls: ACL[];
  label?: string;
}>();

const emit = defineEmits<{ "update:modelValue": [string[]] }>();

const { t } = useI18n();

const query = ref("");
const open = ref(false);
const cursor = ref(-1);
const rootRef = ref<HTMLElement | null>(null);
const inputRef = ref<HTMLInputElement | null>(null);
const listRef = ref<HTMLElement | null>(null);

const selectedAcls = computed(() =>
  props.modelValue
    .map((id) => props.acls.find((a) => a.ACLID === id))
    .filter((a): a is ACL => Boolean(a))
    .sort((a, b) => (a.Description ?? "").localeCompare(b.Description ?? "", undefined, { sensitivity: "base" })),
);

// Alphabetical, so the list reads the same regardless of API order.
const sortedAcls = computed(() =>
  [...props.acls].sort((a, b) =>
    (a.Description ?? "").localeCompare(b.Description ?? "", undefined, { sensitivity: "base" }),
  ),
);

const filtered = computed(() => {
  const q = query.value.trim().toLowerCase();
  if (!q) return sortedAcls.value;
  return sortedAcls.value.filter((a) => (a.Description ?? "").toLowerCase().includes(q));
});

function isSelected(id: string): boolean {
  return props.modelValue.includes(id);
}

function toggle(id: string) {
  const next = isSelected(id) ? props.modelValue.filter((x) => x !== id) : [...props.modelValue, id];
  emit("update:modelValue", next);
}

function clearAll() {
  emit("update:modelValue", []);
}

function onInput() {
  cursor.value = -1;
  open.value = true;
}

function scrollCursorIntoView() {
  nextTick(() => {
    const options = listRef.value?.querySelectorAll<HTMLElement>('[role="option"]');
    options?.[cursor.value]?.scrollIntoView({ block: "nearest" });
  });
}

function onKeydown(e: KeyboardEvent) {
  const list = filtered.value;
  if (e.key === "ArrowDown") {
    e.preventDefault();
    open.value = true;
    cursor.value = Math.min(cursor.value + 1, list.length - 1);
    scrollCursorIntoView();
  } else if (e.key === "ArrowUp") {
    e.preventDefault();
    cursor.value = Math.max(cursor.value - 1, 0);
    scrollCursorIntoView();
  } else if (e.key === "Enter") {
    e.preventDefault();
    if (list[cursor.value]) toggle(list[cursor.value].ACLID);
  } else if (e.key === "Escape") {
    open.value = false;
  }
}

// The label row toggles the dropdown so it can be dismissed in place.
function toggleOpen() {
  open.value = !open.value;
  if (open.value) inputRef.value?.focus();
}

function handleDocumentClick(e: MouseEvent) {
  if (!open.value) return;
  if (rootRef.value && e.composedPath().includes(rootRef.value)) return;
  open.value = false;
}

onMounted(() => document.addEventListener("click", handleDocumentClick));
onBeforeUnmount(() => document.removeEventListener("click", handleDocumentClick));

const labelText = computed(() => props.label ?? t("accessMenu.knocSearch.label"));
</script>

<template>
  <div ref="rootRef">
    <div class="mb-1 flex items-center justify-between">
      <button
        type="button"
        :aria-expanded="open"
        class="text-xs font-medium text-muted-foreground transition-colors hover:text-foreground"
        @click="toggleOpen"
      >
        {{ labelText }}
      </button>
      <span v-if="modelValue.length > 0" class="flex items-center gap-2 text-xs text-muted-foreground">
        {{ t("accessMenu.knocSearch.selectedCount", { count: modelValue.length }) }}
        <button type="button" class="underline transition-colors hover:text-foreground" @click="clearAll">
          {{ t("accessMenu.knocSearch.clearAll") }}
        </button>
      </span>
    </div>

    <div
      class="flex min-h-9 flex-wrap items-center gap-1.5 rounded-md border border-input bg-background px-2 py-1 focus-within:ring-2 focus-within:ring-ring"
    >
      <MagnifyingGlassIcon class="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
      <span
        v-for="acl in selectedAcls"
        :key="acl.ACLID"
        class="inline-flex items-center gap-1 rounded-md border border-green-500/30 bg-green-500/10 py-0.5 pl-1.5 pr-1 text-xs font-medium text-green-600 dark:text-green-500"
      >
        <font-awesome-icon :icon="acl.Icon" fixed-width class="h-3 w-3" />
        {{ acl.Description }}
        <button
          type="button"
          class="opacity-70 hover:opacity-100"
          :aria-label="`${t('common.delete')} ${acl.Description}`"
          @click="toggle(acl.ACLID)"
        >
          <XMarkIcon class="h-3 w-3" aria-hidden="true" />
        </button>
      </span>
      <input
        ref="inputRef"
        v-model="query"
        type="text"
        :aria-label="labelText"
        :placeholder="t('accessMenu.knocSearch.placeholder')"
        class="h-6 min-w-[6rem] flex-1 border-0 bg-transparent text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-0"
        @focus="open = true"
        @input="onInput"
        @keydown="onKeydown"
      />
    </div>

    <!-- In document flow (not absolute) so opening the list pushes content below
         it, like the submit button, down instead of covering it. -->
    <div
      v-if="open"
      ref="listRef"
      role="listbox"
      :aria-label="labelText"
      aria-multiselectable="true"
      class="mt-1 max-h-44 overflow-y-auto rounded-md border border-border bg-background p-1 shadow-lg"
    >
      <p v-if="filtered.length === 0" class="px-2 py-2 text-xs text-muted-foreground">
        {{ t("accessMenu.knocSearch.noResults") }}
      </p>
      <button
        v-for="(acl, idx) in filtered"
        :key="acl.ACLID"
        type="button"
        role="option"
        :aria-selected="isSelected(acl.ACLID)"
        :class="[
          'flex w-full items-center gap-2.5 rounded-md px-2 py-1.5 text-left text-sm transition-colors',
          idx === cursor ? 'bg-accent text-accent-foreground' : 'hover:bg-accent hover:text-accent-foreground',
        ]"
        @click="toggle(acl.ACLID)"
      >
        <CheckboxDecorator :checked="isSelected(acl.ACLID)" />
        <span class="flex h-3.5 w-3.5 shrink-0 items-center justify-center text-muted-foreground">
          <font-awesome-icon v-if="acl.Icon" :icon="acl.Icon" fixed-width class="h-3.5 w-3.5" />
        </span>
        <span class="flex-1 truncate">{{ acl.Description }}</span>
      </button>
    </div>
  </div>
</template>
